import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Rocket, TrendingUp, Megaphone, Landmark, Settings2, BarChart3, HeartHandshake, LayoutGrid,
  LayoutDashboard, LineChart, Target, Users, Presentation, Bot, FileSearch, Flag,
  Search, Sparkles, Linkedin, Youtube, Star, Briefcase, Building2,
  Video, Mail, Loader2, Check, GraduationCap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';
import { planAllowsTeamInvites } from '../../services/postSignupSetup';
import { fetchWithApiFallback } from '../../utils/apiBase';
import { sanitizeUserFacingError } from '../../utils/errorSanitizer';
import { hasCompletedSignupQuestions } from '../../utils/signupQuestions';
import { countryOptions } from '../../utils/countryOptions';
import { markFirstSignupHome } from '../../utils/signupLanding';

const PRODUCT = 'intelligence-navigator';

const ROLE_OPTIONS = [
  { key: 'founder', Icon: Rocket },
  { key: 'sales', Icon: TrendingUp },
  { key: 'marketing', Icon: Megaphone },
  { key: 'finance', Icon: Landmark },
  { key: 'operations', Icon: Settings2 },
  { key: 'data', Icon: BarChart3 },
  { key: 'customerSuccess', Icon: HeartHandshake },
  { key: 'student', Icon: GraduationCap },
  { key: 'other', Icon: LayoutGrid },
];

const USE_CASE_OPTIONS = [
  { key: 'dashboards', Icon: LayoutDashboard },
  { key: 'forecasting', Icon: LineChart },
  { key: 'competitorIntel', Icon: Target },
  { key: 'customerInsights', Icon: Users },
  { key: 'reportsPresentations', Icon: Presentation },
  { key: 'aiAgents', Icon: Bot },
  { key: 'documentProcessing', Icon: FileSearch },
  { key: 'okrs', Icon: Flag },
];

const REFERRAL_OPTIONS = [
  { key: 'search', Icon: Search },
  { key: 'aiAssistants', Icon: Sparkles },
  { key: 'linkedin', Icon: Linkedin },
  { key: 'youtube', Icon: Youtube },
  { key: 'reviewSite', Icon: Star },
  { key: 'friend', Icon: Briefcase },
  { key: 'work', Icon: Building2 },
  { key: 'other', Icon: LayoutGrid },
];

const START_OPTIONS = [
  { key: 'explore', Icon: Rocket },
  { key: 'liveDemo', Icon: Video },
];

const MARKETING_BASE = process.env.REACT_APP_MARKETING_URL || 'https://www.xpherium.com';

function getAuthToken() {
  return localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
}

// Same wash as the signup page brand panel (AuthBrandPanel).
const SIGNUP_WASH = {
  background: 'linear-gradient(110deg, #eef4fb 0%, #ecf2fb 42%, #f2f6fc 70%, #f8fbfe 100%)',
};
const SIGNUP_GLOW = {
  background: 'radial-gradient(115% 80% at 0% 0%, rgba(222,196,236,0.55) 0%, rgba(222,196,236,0.18) 32%, transparent 58%)',
};

function QuestionScreen({ children, className = '' }) {
  return (
    <div className={`relative min-h-screen ${className}`} style={SIGNUP_WASH}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={SIGNUP_GLOW} />
      {children}
    </div>
  );
}

const stepMotion = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.22, ease: 'easeOut' },
};

function readOnboardingDraft() {
  try {
    if (!sessionStorage.getItem('xph_trial_checkout')) return null;
    const parsed = JSON.parse(sessionStorage.getItem('xph_onboarding_draft') || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export default function OnboardingQuestions({
  pendingMode = false,
  onPendingComplete = null,
  paymentSlot = null,
  paymentReady = false,
  paymentFormReady = false,
  paymentButtonVisible = false,
  onPaymentContinue = null,
  paymentContinueLabel = null,
  paymentNote = null,
}) {
  const includePayment = Boolean(paymentSlot);
  const totalSteps = includePayment ? 5 : 4;
  const paymentStep = includePayment ? 4 : null;
  const startStep = includePayment ? 5 : 4;
  const { user, updateUser } = useAuth();
  const { t, i18n } = useTranslation('onboarding');
  const navigate = useNavigate();

  const draft = pendingMode ? readOnboardingDraft() : null;
  const [step, setStep] = useState(draft?.step || 1);
  const [role, setRole] = useState(draft?.role || null);
  const [useCases, setUseCases] = useState(Array.isArray(draft?.useCases) ? draft.useCases : []);
  const [country, setCountry] = useState(draft?.country || '');
  const [referralSource, setReferralSource] = useState(draft?.referralSource || null);
  const [startPreference, setStartPreference] = useState(draft?.startPreference || 'explore');
  const [submitting, setSubmitting] = useState(false);
  const [statusReady, setStatusReady] = useState(() => pendingMode || hasCompletedSignupQuestions(user));

  // Invite step (paid plans with open seats only)
  const [inviteEligible, setInviteEligible] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [seatsRemaining, setSeatsRemaining] = useState(0);
  const [inviteEmails, setInviteEmails] = useState(['', '', '']);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [showInviteStep, setShowInviteStep] = useState(false);

  useEffect(() => {
    if (pendingMode) return undefined;
    if (hasCompletedSignupQuestions(user)) {
      try {
        if (localStorage.getItem('newUser') === 'true') markFirstSignupHome(user?.id || user?.email);
      } catch { /* ignore */ }
      navigate('/dashboard', { replace: true });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithApiFallback('onboarding/questions', {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.completed) {
          try {
            if (localStorage.getItem('newUser') === 'true') markFirstSignupHome(user?.id || user?.email);
          } catch { /* ignore */ }
          updateUser({
            companyProfile: data.answers
              ? { ...(user?.companyProfile || {}), onboarding: data.answers }
              : user?.companyProfile,
            settings: {
              ...(user?.settings || {}),
              onboardingQuestionsRequired: false,
              onboardingQuestionsCompleted: true,
            },
          });
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        /* fall through and show the form from the in-memory user flag */
      }
      if (!cancelled) setStatusReady(true);
    })();
    return () => { cancelled = true; };
  }, [user?.id, pendingMode]);

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const status = await subscriptionService.getCurrentSubscription(PRODUCT);
        const plan = status?.subscription?.plan || 'free';
        if (cancelled || !planAllowsTeamInvites(plan)) return;
        const res = await fetchWithApiFallback(`/teams/${PRODUCT}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const data = await res.json();
        const team = data?.data;
        if (cancelled || !data?.success || !team?.id) return;
        const limit = typeof team.seatLimit === 'number' ? team.seatLimit : null;
        const totalSeats = (team.members?.length || 0) + (team.invitations?.length || 0);
        const remaining = limit != null && limit !== -1 ? Math.max(0, limit - totalSeats) : 3;
        if (remaining > 0 && limit !== 1) {
          setTeamId(team.id);
          setSeatsRemaining(remaining);
          setInviteEligible(true);
        }
      } catch {
        /* invite step simply doesn't show */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!pendingMode) return undefined;
    try {
      sessionStorage.setItem('xph_onboarding_draft', JSON.stringify({
        step, role, useCases, country, referralSource, startPreference,
      }));
    } catch { /* ignore */ }
    return undefined;
  }, [pendingMode, step, role, useCases, country, referralSource, startPreference]);

  const countries = useMemo(
    () => countryOptions(i18n.language || 'en'),
    [i18n.language]
  );

  const stepValid = useMemo(() => {
    if (step === 1) return Boolean(role) && /^[A-Z]{2}$/.test(country);
    if (step === 2) return useCases.length > 0;
    if (step === 3) return Boolean(referralSource);
    if (step === paymentStep) return Boolean(paymentReady || paymentFormReady);
    if (step === startStep) return Boolean(startPreference);
    return true;
  }, [step, role, useCases, country, referralSource, startPreference, paymentStep, startStep, paymentReady, paymentFormReady]);

  const toggleUseCase = (key) => {
    setUseCases((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const finish = async () => {
    const answers = {
      role,
      useCases,
      country,
      referralSource,
      startPreference,
      locale: i18n.language || 'en',
    };
    if (pendingMode && onPendingComplete) {
      setSubmitting(true);
      try {
        await onPendingComplete(answers);
      } catch (err) {
        toast.error(sanitizeUserFacingError(err.message, t('welcomeFlow.saveFailed')));
        if (
          paymentStep
          && ['CHECKOUT_INCOMPLETE', 'CHECKOUT_MISMATCH', 'CARD_NOT_VERIFIED', 'PAYMENT_METHOD_REQUIRED', 'PAYMENT_FAILED'].includes(err?.code)
        ) {
          setStep(paymentStep);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetchWithApiFallback('onboarding/questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(answers),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to save your answers');
      }
      updateUser({ companyProfile: data.companyProfile, settings: data.settings });
      markFirstSignupHome(user?.id || user?.email);
      if (startPreference === 'liveDemo') {
        try {
          window.open(`${MARKETING_BASE}/contact-sales`, '_blank', 'noopener,noreferrer');
        } catch { /* dashboard is still the destination */ }
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(sanitizeUserFacingError(err.message, t('welcomeFlow.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (!stepValid || submitting) return;
    if (step === paymentStep) {
      setSubmitting(true);
      try {
        const ok = onPaymentContinue ? await onPaymentContinue() : false;
        if (ok) setStep(step + 1);
      } catch (err) {
        toast.error(sanitizeUserFacingError(err.message, t('welcomeFlow.payment.required')));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (step < totalSteps) {
      setStep(step + 1);
      return;
    }
    if (inviteEligible && !showInviteStep) {
      setShowInviteStep(true);
      return;
    }
    finish();
  };

  const handleBack = () => {
    if (showInviteStep) {
      setShowInviteStep(false);
      return;
    }
    if (step > 1) setStep(step - 1);
  };

  const validInviteEmails = useMemo(() => {
    const seen = new Set();
    return inviteEmails
      .map((e) => e.trim().toLowerCase())
      .filter((email) => {
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || seen.has(email)) return false;
        seen.add(email);
        return true;
      });
  }, [inviteEmails]);

  const sendInvitesAndFinish = async () => {
    if (!teamId || validInviteEmails.length === 0) {
      finish();
      return;
    }
    setSendingInvites(true);
    let sent = 0;
    try {
      for (const email of validInviteEmails.slice(0, seatsRemaining)) {
        const res = await fetchWithApiFallback(`/teams/${teamId}/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ email, role: 'member' }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.success) sent += 1;
      }
      if (sent > 0) toast.success(t('welcomeFlow.invite.sentToast', { count: sent }));
    } catch {
      /* invites are best-effort; the questionnaire answers still save */
    } finally {
      setSendingInvites(false);
      finish();
    }
  };

  const tileBase =
    'group flex flex-col items-center justify-center gap-2.5 rounded-xl border bg-white px-4 py-6 text-center transition-all duration-150 cursor-pointer select-none';
  const tileIdle =
    'border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-slate-300 hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)] hover:-translate-y-px';
  const tileActive =
    'border-blue-600 ring-1 ring-blue-600 bg-blue-50/60 shadow-[0_4px_14px_rgba(37,99,235,0.12)]';

  const renderTileGrid = (options, groupKey, selected, onSelect) => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 max-w-2xl mx-auto">
      {options.map(({ key, Icon }) => {
        const active = selected === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={`${tileBase} ${active ? tileActive : tileIdle}`}
          >
            <Icon
              className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-slate-500 group-hover:text-slate-700'}`}
              strokeWidth={1.75}
            />
            <span className={`text-[13px] font-medium leading-tight ${active ? 'text-blue-700' : 'text-slate-700'}`}>
              {t(`welcomeFlow.${groupKey}.options.${key}`)}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderCountrySelect = () => (
    <label className="block max-w-2xl mx-auto mb-4">
      <span className="block text-[13px] font-medium text-slate-700 mb-1.5">
        {t('welcomeFlow.role.countryLabel')}
      </span>
      <select
        value={country}
        onChange={(event) => setCountry(event.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[13px] text-slate-800 shadow-[0_1px_2px_rgba(15,23,42,0.04)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
      >
        <option value="">{t('welcomeFlow.role.countryPlaceholder')}</option>
        {countries.map((option) => (
          <option key={option.code} value={option.code}>{option.name}</option>
        ))}
      </select>
    </label>
  );

  const renderUseCases = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
      {USE_CASE_OPTIONS.map(({ key, Icon }) => {
        const active = useCases.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggleUseCase(key)}
            className={`flex items-center gap-3 rounded-xl border bg-white px-4 py-3.5 text-left transition-all duration-150 cursor-pointer select-none ${
              active ? tileActive : tileIdle
            }`}
          >
            <Icon
              className={`w-[18px] h-[18px] shrink-0 ${active ? 'text-blue-600' : 'text-slate-500'}`}
              strokeWidth={1.75}
            />
            <span className={`flex-1 text-[13px] font-medium ${active ? 'text-blue-700' : 'text-slate-700'}`}>
              {t(`welcomeFlow.useCases.options.${key}`)}
            </span>
            <span
              className={`flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors ${
                active ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'
              }`}
            >
              {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderStartCards = () => (
    <div className="max-w-xl mx-auto space-y-3">
      {START_OPTIONS.map(({ key, Icon }) => {
        const active = startPreference === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setStartPreference(key)}
            className={`flex w-full items-center gap-3.5 rounded-xl border bg-white px-5 py-4 text-left transition-all duration-150 cursor-pointer select-none ${
              active ? tileActive : tileIdle
            }`}
          >
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
                active ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-200 bg-slate-50 text-slate-500'
              }`}
            >
              <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
            </span>
            <span className="flex-1">
              <span className={`block text-sm font-semibold ${active ? 'text-blue-700' : 'text-slate-800'}`}>
                {t(`welcomeFlow.start.options.${key}.title`)}
              </span>
              <span className="block text-[13px] text-slate-500 mt-0.5">
                {t(`welcomeFlow.start.options.${key}.subtitle`)}
              </span>
              {key === 'liveDemo' && (
                <span className="block text-[12px] text-slate-400 mt-1">
                  {t(`welcomeFlow.start.options.${key}.note`)}
                </span>
              )}
            </span>
            <span
              className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 transition-colors ${
                active ? 'border-blue-600' : 'border-slate-300'
              }`}
            >
              {active && <span className="h-2 w-2 rounded-full bg-blue-600" />}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderInviteStep = () => (
    <div className="max-w-md mx-auto">
      <div className="space-y-3">
        {inviteEmails.slice(0, Math.max(1, Math.min(3, seatsRemaining))).map((email, index) => (
          <div key={index} className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) =>
                setInviteEmails((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
              }
              placeholder={t('welcomeFlow.invite.emailPlaceholder')}
              className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 shadow-[0_1px_2px_rgba(15,23,42,0.04)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
        ))}
      </div>
    </div>
  );

  const headerFor = () => {
    if (showInviteStep) {
      return { title: t('welcomeFlow.invite.title'), subtitle: t('welcomeFlow.invite.subtitle') };
    }
    if (step === paymentStep) {
      return { title: t('welcomeFlow.payment.title'), subtitle: t('welcomeFlow.payment.subtitle') };
    }
    const keys = { 1: 'role', 2: 'useCases', 3: 'referral' };
    const k = step === startStep ? 'start' : keys[step];
    return { title: t(`welcomeFlow.${k}.title`), subtitle: t(`welcomeFlow.${k}.subtitle`) };
  };

  const { title, subtitle } = headerFor();
  const contentKey = showInviteStep ? 'invite' : `step-${step}`;

  if (!pendingMode && (!statusReady || hasCompletedSignupQuestions(user))) {
    return (
      <QuestionScreen className="flex items-center justify-center">
        <Loader2 className="relative z-10 w-6 h-6 animate-spin text-slate-400" />
      </QuestionScreen>
    );
  }

  return (
    <QuestionScreen className="flex flex-col">
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-9">
            <h1 className={step === paymentStep
              ? 'text-center text-[32px] font-semibold tracking-[-0.03em] text-[#16161d] sm:text-[36px]'
              : 'text-[26px] sm:text-3xl font-bold tracking-tight text-slate-900'}>
              {title}
            </h1>
            {step !== paymentStep && (
              <p className="mt-2.5 text-[15px] text-slate-500">{subtitle}</p>
            )}
          </div>

          {step !== paymentStep && (
          <AnimatePresence mode="wait">
            <motion.div key={contentKey} {...stepMotion}>
              {showInviteStep && renderInviteStep()}
              {!showInviteStep && step === 1 && (
                <>
                  {renderCountrySelect()}
                  {renderTileGrid(ROLE_OPTIONS, 'role', role, setRole)}
                </>
              )}
              {!showInviteStep && step === 2 && renderUseCases()}
              {!showInviteStep && step === 3 &&
                renderTileGrid(REFERRAL_OPTIONS, 'referral', referralSource, setReferralSource)}
              {!showInviteStep && step === startStep && renderStartCards()}

              <div className="mt-10 max-w-2xl mx-auto flex items-center justify-between">
                <div className="w-28">
                  {(step > 1 || showInviteStep) && (
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={submitting || sendingInvites}
                      className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      {t('welcomeFlow.back')}
                    </button>
                  )}
                </div>

                <span className="text-[13px] text-slate-400 font-medium">
                  {!showInviteStep &&
                    t('welcomeFlow.progress', { current: step, total: totalSteps })}
                </span>

                <div className="w-40 flex justify-end">
                  {showInviteStep ? (
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={finish}
                        disabled={submitting || sendingInvites}
                        className="text-[13px] font-medium text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {t('welcomeFlow.invite.skip')}
                      </button>
                      <button
                        type="button"
                        onClick={sendInvitesAndFinish}
                        disabled={submitting || sendingInvites || validInviteEmails.length === 0}
                        className={`inline-flex items-center gap-2 rounded-lg px-5 py-2 text-[13px] font-semibold transition-colors ${
                          validInviteEmails.length > 0 && !sendingInvites && !submitting
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-[0_2px_8px_rgba(37,99,235,0.35)]'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        {(sendingInvites || submitting) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {t('welcomeFlow.invite.send')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleContinue}
                      disabled={!stepValid || submitting}
                      className={`inline-flex items-center gap-2 rounded-lg px-5 py-2 text-[13px] font-semibold transition-colors ${
                        stepValid && !submitting
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-[0_2px_8px_rgba(37,99,235,0.35)]'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {t('welcomeFlow.continue')}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          )}

          {includePayment && (
            <div className={step === paymentStep ? '' : 'pointer-events-none fixed left-0 top-0 z-[-1] w-[440px] opacity-0'}>
              {paymentSlot}
            </div>
          )}

          {step === paymentStep && (
            <div className="mx-auto mt-6 max-w-[440px]">
              {(paymentReady || paymentButtonVisible) && (
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!stepValid || submitting}
                  className={`flex w-full items-center justify-center gap-2 rounded-full py-4 text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors ${
                    stepValid && !submitting
                      ? 'bg-[#16161d] text-white hover:bg-black'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {paymentContinueLabel || t('welcomeFlow.payment.getPlan')}
                </button>
              )}
              {paymentNote && (
                <p className="mt-4 text-left text-[12px] leading-relaxed text-[#8b93a7]">{paymentNote}</p>
              )}
              <div className="mt-6 flex items-center justify-between">
                <div className="w-28">
                  <button
                    type="button"
                    onClick={handleBack}
                    disabled={submitting}
                    className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    {t('welcomeFlow.back')}
                  </button>
                </div>
                <span className="text-[13px] font-medium text-slate-400">
                  {t('welcomeFlow.progress', { current: step, total: totalSteps })}
                </span>
                <span className="w-28" />
              </div>
            </div>
          )}
        </div>
      </div>
    </QuestionScreen>
  );
}
